<script setup lang="ts">
import { onScopeDispose } from 'vue'
import { onAuthContextChanged } from '@/@vxapp/Auth/authContext'
import { computed, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'
import type { AnyAbility } from '@casl/ability'
import { A, Action } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import { Endpoint } from '@/@vxapp/@Enums/Endpoint'
import { Errors } from '@/@vxapp/@Enums/Error'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { QuoteField } from '@/@vxapp/@Enums/PHP/ServicesV2/Quotes/DataProviders/QuoteField'
import { QuoteStatus } from '@/@vxapp/@Enums/PHP/ServicesV2/Quotes/DataProviders/QuoteStatus'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { ServiceType } from '@/@vxapp/@Enums/ServiceType'
import { useQuoteStore } from '@/@vxapp/Quotes/useQuoteStore'
import { deepCopy } from '@/@vxapp/@Utils/helpers'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import HoardingPlanDialog from '@/components/Dialogs/HoardingPlanDialog.vue'
import HoardingPlannerAccessDeniedDialog from '@/components/Tools/Hoardings/Dialogs/HoardingPlannerAccessDeniedDialog.vue'
import {
  HOARDING_PLANNER_DENIED_TEXT,
  HoardingPlannerOpener,
} from '@/components/Tools/Hoardings/constants/hoardingPlannerUi'
import { useHoardingPlannerRoute } from '@/components/Tools/Hoardings/composables/useHoardingPlannerRoute'
import QuotePurchaseOrderDialog from '@/components/Dialogs/Quotes/QuotePurchaseOrderDialog.vue'
import QuoteBulkInvoiceActions from '@/components/ServiceLists/Quotes/QuoteBulkInvoiceActions.vue'
import QuoteBulkStatusActions from '@/components/ServiceLists/Quotes/QuoteBulkStatusActions.vue'
import QuoteListToolbar from '@/components/ServiceLists/Quotes/QuoteListToolbar.vue'
import ServiceListToolsPanel from '@/components/ServiceLists/ServiceListToolsPanel.vue'
import QuoteOperationsList from '@/components/ServiceLists/Quotes/QuoteOperationsList.vue'
import type {
  QuoteListBooleanFilter,
  QuoteListQueryParams,
  QuoteListRecord,
  QuotePaginationMeta,
} from '@/components/ServiceLists/Quotes/quoteListTypes'
import {
  QuoteListBooleanFilter as QuoteBooleanFilter,
  QuoteListSort,
  QuoteListToolMode,
} from '@/components/ServiceLists/Quotes/quoteListTypes'
import QuotesListInsights from '@/components/ServiceLists/QuotesListInsights.vue'
import TableMessagesTool from '@/components/Tables/TableMessagesTool.vue'
import {
  useBulkServiceStatusUpdate,
  type BulkStatusService,
} from '@/composables/useBulkServiceStatusUpdate'
import { can, canRecord, canRecordChange } from '@/@layouts/plugins/casl'
import { Subject } from '@/@vxapp/@Enums/PHP/Auth/Ability/Subjects/DataProviders/AbilitySubject'
import { ability } from '@/plugins/casl/composables/useAbility'
import { useAppNotifications } from '@/plugins/toast/composables/useAppNotifications'

definePage({ meta: { ...PageMeta.Quotes.List.Meta, pageTitle: PageMeta.Quotes.List.Title } })

interface DialogVisibilityParams {
  visibility: boolean
  hasCancelled?: boolean
}

const defaultQueryParams: QuoteListQueryParams = {
  sort: QuoteListSort.Newest,
  page: 1,
  limit: 10,
  search: undefined,
  hideInactive: true,
  filterByPendingPO: false,
  filterByIsRecurring: false,
  filterByEmailSet: false,
  generatedThisMonth: false,
  generatedDateFrom: undefined,
  generatedDateTo: undefined,
  status: undefined,
}

const managedQueryKeys = Object.keys(defaultQueryParams) as Array<keyof QuoteListQueryParams>

const log = createLogger('QuotesListPage')
const quoteAddRouteName = PageMeta.Quotes.Add.Name as 'apps-quote-add'
const invoiceAddRouteName = PageMeta.Invoices.Add.Name as 'apps-invoice-add'
const quoteAbility = ability as unknown as AnyAbility | null
const route = useRoute()
const router = useRouter()
const quoteStore = useQuoteStore()
const notify = useAppNotifications()

const { isUpdating: isBulkStatusSubmitting, updateStatuses } =
  useBulkServiceStatusUpdate('QuotesListStatusUpdate')

const quoteOperationsList =
  useTemplateRef<InstanceType<typeof QuoteOperationsList>>('quoteOperationsList')

const isRouteQueryReady = shallowRef(false)
const activeTool = shallowRef<QuoteListToolMode | null>(null)

const canViewCount = computed(
  () =>
    quoteAbility?.can(Action.READ, Subject.QUOTES_PREVIEW_ALL, 'paginationMeta.entriesFound') ??
    false
)

const resultCount = shallowRef<number | null>(null)

const {
  isPlannerOpen: isHoardingPlanDialogVisible,
  canOpenPlanner: canOpenHoardingPlanCreator,
  isPlannerDenied: isHoardingPlannerDenied,
  planGUID: hoardingPlanGUID,
  serviceGUID: hoardingServiceGUID,
  openPlanner: handleHoardingPlanDialogOpen,
  handlePlannerVisibility: handleHoardingPlanDialogVisibility,
  updatePlannerPlan,
} = useHoardingPlannerRoute(null, HoardingPlannerOpener.QUOTE_LIST)

const isPurchaseOrderDialogVisible = shallowRef(false)
const selectedQuoteForPurchaseOrder = ref<QuoteListRecord | null>(null)
const isPurchaseOrderLoading = shallowRef(false)
const setStatusOnSubmit = shallowRef(false)
const invoiceStatusId = shallowRef<number>(QuoteStatus.INACTIVE)
const bulkStatusId = shallowRef<QuoteStatus>(QuoteStatus.ACTIVE)
const isInvoiceSubmitting = shallowRef(false)
let internallySyncedRouteQueryKey: string | null = null

const queryParams = computed<QuoteListQueryParams>(() =>
  normalizeQueryParams(quoteStore.queryParams as Record<string, unknown> | null)
)

const showSelect = computed(() => activeTool.value !== null)
const isInvoiceMode = computed(() => activeTool.value === QuoteListToolMode.Invoice)
const isMessageMode = computed(() => activeTool.value === QuoteListToolMode.Message)
const isStatusMode = computed(() => activeTool.value === QuoteListToolMode.Status)
const selectedCount = computed(() => quoteStore.selectedQuotes?.length ?? 0)

function normalizeQueryValue(value: unknown) {
  const normalizedValue = Array.isArray(value) ? value[0] : value

  return typeof normalizedValue === 'string' || typeof normalizedValue === 'number'
    ? String(normalizedValue)
    : undefined
}

function normalizeBoolean(value: unknown, fallback: boolean) {
  if (typeof value === 'boolean') return value

  const normalizedValue = normalizeQueryValue(value)

  if (normalizedValue === 'true') return true
  if (normalizedValue === 'false') return false

  return fallback
}

function normalizePositiveInteger(value: unknown, fallback: number) {
  const parsedValue = Number.parseInt(normalizeQueryValue(value) ?? '', 10)

  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback
}

function normalizeQueryParams(source: Record<string, unknown> | null): QuoteListQueryParams {
  const search = normalizeQueryValue(source?.search)?.trim()
  const sort = normalizeQueryValue(source?.sort)
  const generatedDateFrom = normalizeQueryValue(source?.generatedDateFrom)
  const generatedDateTo = normalizeQueryValue(source?.generatedDateTo)
  const normalizedStatus = normalizePositiveInteger(source?.status, 0)

  return {
    ...defaultQueryParams,
    page: normalizePositiveInteger(source?.page, defaultQueryParams.page),
    limit: normalizePositiveInteger(source?.limit, defaultQueryParams.limit),
    sort: sort || defaultQueryParams.sort,
    search: search || undefined,
    hideInactive: normalizeBoolean(source?.hideInactive, defaultQueryParams.hideInactive),
    filterByPendingPO: normalizeBoolean(
      source?.filterByPendingPO,
      defaultQueryParams.filterByPendingPO
    ),
    filterByIsRecurring: normalizeBoolean(
      source?.filterByIsRecurring,
      defaultQueryParams.filterByIsRecurring
    ),
    filterByEmailSet: normalizeBoolean(
      source?.filterByEmailSet,
      defaultQueryParams.filterByEmailSet
    ),
    generatedThisMonth: normalizeBoolean(
      source?.generatedThisMonth,
      defaultQueryParams.generatedThisMonth
    ),
    generatedDateFrom: generatedDateFrom || undefined,
    generatedDateTo: generatedDateTo || undefined,
    status: normalizedStatus || undefined,
  }
}

function queryParamsMatch(left: QuoteListQueryParams, right: QuoteListQueryParams) {
  return managedQueryKeys.every(key => left[key] === right[key])
}

function applyRouteQuery() {
  const currentQueryParams = normalizeQueryParams(
    quoteStore.queryParams as Record<string, unknown> | null
  )

  const routeQueryParams = normalizeQueryParams(route.query)

  if (queryParamsMatch(currentQueryParams, routeQueryParams)) return false

  Object.assign(quoteStore.queryParams, currentQueryParams, routeQueryParams)

  return true
}

function buildRouteQuery() {
  const nextQuery = { ...route.query }
  const normalizedQueryParams = queryParams.value

  for (const key of managedQueryKeys) delete nextQuery[key]

  for (const key of managedQueryKeys) {
    const value = normalizedQueryParams[key]

    if (value !== defaultQueryParams[key] && value !== undefined) nextQuery[key] = String(value)
  }

  return nextQuery
}

async function syncRouteQuery() {
  const nextQuery = buildRouteQuery()
  const nextQueryKey = JSON.stringify(nextQuery)

  if (JSON.stringify(route.query) === nextQueryKey) return

  internallySyncedRouteQueryKey = nextQueryKey
  await router.replace({ query: nextQuery })
}

async function refreshQuotes(requestedQueryParams: QuoteListQueryParams = queryParams.value) {
  await quoteOperationsList.value?.fetchQuotes(requestedQueryParams)
}

async function updateQueryParams(patch: Partial<QuoteListQueryParams>) {
  const requestedQueryParams = normalizeQueryParams({ ...queryParams.value, ...patch })

  Object.assign(quoteStore.queryParams, requestedQueryParams)

  if (isRouteQueryReady.value) await syncRouteQuery()

  await refreshQuotes(requestedQueryParams)
}

async function handleSearch(search: string) {
  await updateQueryParams({ search: search || undefined, page: 1 })
}

async function handleStatusChange(status: number | null) {
  await updateQueryParams({ status: status ?? undefined, page: 1 })
}

async function handleFilterChange(key: QuoteListBooleanFilter, value: boolean) {
  const patch: Partial<QuoteListQueryParams> = { page: 1 }

  if (key === QuoteBooleanFilter.HideInactive) patch.hideInactive = value
  if (key === QuoteBooleanFilter.PendingPurchaseOrder) {
    patch.filterByPendingPO = value
    if (value) patch.filterByIsRecurring = false
  }
  if (key === QuoteBooleanFilter.Recurring) {
    patch.filterByIsRecurring = value
    if (value) patch.filterByPendingPO = false
  }
  if (key === QuoteBooleanFilter.HasEmail) patch.filterByEmailSet = value
  if (key === QuoteBooleanFilter.GeneratedThisMonth) {
    patch.generatedThisMonth = value
    if (value) {
      patch.generatedDateFrom = undefined
      patch.generatedDateTo = undefined
    }
  }

  await updateQueryParams(patch)
}

async function handleDateRangeChange(from?: string, to?: string) {
  await updateQueryParams({
    generatedDateFrom: from,
    generatedDateTo: to,
    generatedThisMonth: false,
    page: 1,
  })
}

async function handleSortChange(sort: string) {
  await updateQueryParams({ sort, page: 1 })
}

async function handleLimitChange(limit: number) {
  await updateQueryParams({ limit, page: 1 })
}

async function handlePageChange(page: number) {
  await updateQueryParams({ page })
}

async function handleClearFilters() {
  Object.assign(quoteStore.queryParams, defaultQueryParams)

  if (isRouteQueryReady.value) await syncRouteQuery()

  await refreshQuotes()
}

function handleListLoaded(pagination: QuotePaginationMeta) {
  resultCount.value = pagination.entriesFound
}

const toolOrigin = shallowRef<DOMRect>()

function activateTool(tool: QuoteListToolMode, event?: MouseEvent) {
  toolOrigin.value = (event?.currentTarget as HTMLElement | undefined)?.getBoundingClientRect()
  activeTool.value = activeTool.value === tool ? null : tool
  quoteStore.resetSelectedItems()
}

function handleInvoiceMode(event?: MouseEvent) {
  if (!can(A.CREATE, Subject.INVOICES_CREATE)) return
  activateTool(QuoteListToolMode.Invoice, event)
}

function handleMessageMode(event?: MouseEvent) {
  if (
    !(
      can(A.UPDATE, 'Quotes:Messages:Update:Guid') &&
      can(A.READ, Subject.MESSAGES_TEMPLATES_PREVIEW_ALL)
    )
  )
    return
  activateTool(QuoteListToolMode.Message, event)
}

function handleStatusMode(event?: MouseEvent) {
  if (!can(A.UPDATE, Subject.QUOTES_UPDATE_GUID)) return
  activateTool(QuoteListToolMode.Status, event)
}

function handleCloseActiveTool() {
  activeTool.value = null
  quoteStore.resetSelectedItems()
}

function handleNewQuoteNavigation() {
  if (!can(A.CREATE, Subject.QUOTES_CREATE)) return
  router.push({ name: quoteAddRouteName })
}

function handleSelectionChange(guid: string, selected: boolean) {
  const storeSelectedQuotes = quoteStore.selectedQuotes as unknown as string[]
  const selectedQuotes = new Set<string>(storeSelectedQuotes)

  if (selected) selectedQuotes.add(guid)
  else selectedQuotes.delete(guid)

  storeSelectedQuotes.splice(0, storeSelectedQuotes.length, ...selectedQuotes)
}

async function handleSetInactive(quote: QuoteListRecord) {
  if (
    !canRecordChange(
      Action.UPDATE,
      'Quotes:Update:Guid',
      quote,
      { ...quote, [QuoteField.STATUS]: QuoteStatus.INACTIVE },
      QuoteField.STATUS
    )
  )
    return
  if (!canRecord(Action.UPDATE, 'Quotes:Update:Guid', quote, QuoteField.STATUS)) {
    notify.error({
      error: Errors.Auth.ACTION_FORBIDDEN,
      pageSubject: 'Quotes:Update:Guid',
    })

    return
  }

  const { message, responseCode } = await quoteStore.setInactive(quote)

  if (responseCode === ResponseCode.OK) notify.success(message)
  else notify.error(message)

  await refreshQuotes()
}

function handlePurchaseOrderOpen(quote: QuoteListRecord) {
  if (!canRecord(Action.UPDATE, 'Quotes:Update:Guid', quote, QuoteField.PURCHASE_ORDER)) return
  selectedQuoteForPurchaseOrder.value = deepCopy(quote) as QuoteListRecord
  isPurchaseOrderDialogVisible.value = true
}

function handlePurchaseOrderDialogVisibility(params: DialogVisibilityParams) {
  if (isPurchaseOrderLoading.value && !params.visibility) return

  isPurchaseOrderDialogVisible.value = params.visibility

  if (!params.visibility && params.hasCancelled) selectedQuoteForPurchaseOrder.value = null
}

async function handlePurchaseOrderUpdate(updatedQuote: QuoteListRecord) {
  if (
    !canRecord(
      Action.UPDATE,
      'Quotes:Update:Guid',
      selectedQuoteForPurchaseOrder.value,
      QuoteField.PURCHASE_ORDER
    )
  )
    return
  const purchaseOrder = updatedQuote[QuoteField.PURCHASE_ORDER]
  if (
    !canRecordChange(
      Action.UPDATE,
      'Quotes:Update:Guid',
      selectedQuoteForPurchaseOrder.value,
      {
        ...selectedQuoteForPurchaseOrder.value,
        [QuoteField.PURCHASE_ORDER]: purchaseOrder,
        [QuoteField.STATUS]: QuoteStatus.PENDING_INVOICE,
      },
      QuoteField.PURCHASE_ORDER
    )
  )
    return

  if (
    !canRecordChange(
      Action.UPDATE,
      'Quotes:Update:Guid',
      selectedQuoteForPurchaseOrder.value,
      {
        ...selectedQuoteForPurchaseOrder.value,
        [QuoteField.PURCHASE_ORDER]: purchaseOrder,
        [QuoteField.STATUS]: QuoteStatus.PENDING_INVOICE,
      },
      QuoteField.STATUS
    )
  )
    return

  if (!selectedQuoteForPurchaseOrder.value || !purchaseOrder) {
    notify.error('Invalid purchase order data')

    return
  }

  isPurchaseOrderLoading.value = true

  try {
    const { message, responseCode } = await quoteStore.setPurchaseOrder(
      selectedQuoteForPurchaseOrder.value,
      purchaseOrder
    )

    if (responseCode === ResponseCode.OK || responseCode === ResponseCode.CREATED) {
      notify.success(message)
      await refreshQuotes()
    } else {
      notify.error(message || 'Failed to update purchase order')
    }
  } catch (error) {
    log.error('Purchase order update failed', error)
    notify.error('An error occurred while updating the purchase order')
  } finally {
    isPurchaseOrderLoading.value = false
    selectedQuoteForPurchaseOrder.value = null
    isPurchaseOrderDialogVisible.value = false
  }
}

function handleUpdateStatusChange(value: boolean) {
  setStatusOnSubmit.value = value
}

function handleInvoiceStatusUpdate(value: number) {
  invoiceStatusId.value = value
}

function handleBulkStatusUpdate(value: QuoteStatus) {
  bulkStatusId.value = value
}

async function updateSelectedStatuses(status: number) {
  const selected = quoteStore.selectedQuotes as unknown as string[]
  const records = (Array.isArray(quoteStore.quotes) ? quoteStore.quotes : []) as QuoteListRecord[]
  if (
    !selected.every(guid =>
      canRecordChange(
        Action.UPDATE,
        Subject.QUOTES_UPDATE_GUID,
        records.find(quote => quote.quoteGUID === guid),
        { ...records.find(quote => quote.quoteGUID === guid), [QuoteField.STATUS]: status },
        QuoteField.STATUS
      )
    )
  ) {
    notify.error('You do not have permission to update the selected quote statuses.')

    return { updatedCount: 0, failures: [] }
  }

  const result = await updateStatuses({
    serviceGuids: quoteStore.selectedQuotes as unknown as string[],
    serviceType: ServiceType.Quotes as unknown as BulkStatusService,
    status,
    records,
  })

  if (result.updatedCount > 0) {
    notify.success(
      `${result.updatedCount} ${result.updatedCount === 1 ? 'quote status was' : 'quote statuses were'} updated`
    )
  }

  if (result.failures.length > 0) {
    notify.error(
      `${result.failures.length} ${
        result.failures.length === 1 ? 'quote status update failed' : 'quote status updates failed'
      }`
    )
  }

  return result
}

async function handleApplyBulkStatus() {
  if (selectedCount.value === 0) return

  await updateSelectedStatuses(bulkStatusId.value)
  handleCloseActiveTool()
  await refreshQuotes()
}

async function handleInvoiceSelected() {
  if (!can(A.CREATE, Subject.INVOICES_CREATE)) return
  if (selectedCount.value === 0) return

  isInvoiceSubmitting.value = true

  try {
    if (setStatusOnSubmit.value) await updateSelectedStatuses(invoiceStatusId.value)

    await router.push({
      name: invoiceAddRouteName,
      query: {
        isMultiple: 'true',
        selectedQuotes: [...(quoteStore.selectedQuotes as unknown as string[])],
      },
    })
    quoteStore.resetSelectedItems()
  } finally {
    isInvoiceSubmitting.value = false
  }
}

async function handleTableMessagesToolReload() {
  quoteStore.resetSelectedItems()
  await refreshQuotes()
}

onMounted(async () => {
  quoteStore.resetDataTableState()
  quoteStore.setDataTableState({
    paginationMeta: quoteStore.paginationMeta,
    queryParams: defaultQueryParams,
    staticSearchParams: quoteStore.staticSearchParams,
  })
  applyRouteQuery()
  isRouteQueryReady.value = true
  await refreshQuotes()
})

watch(
  () => route.query,
  async () => {
    const routeQueryKey = JSON.stringify(route.query)
    const wasSyncedInternally = internallySyncedRouteQueryKey === routeQueryKey

    if (wasSyncedInternally) internallySyncedRouteQueryKey = null

    if (!isRouteQueryReady.value || wasSyncedInternally || !applyRouteQuery()) return

    await refreshQuotes()
  },
  { deep: true }
)
onScopeDispose(
  onAuthContextChanged(() => {
    activeTool.value = null
    resultCount.value = 0
    selectedQuoteForPurchaseOrder.value = null
    isPurchaseOrderDialogVisible.value = false
    isPurchaseOrderLoading.value = false
    isInvoiceSubmitting.value = false
  })
)
</script>

<template>
  <section
    class="quotes-workspace"
    :class="{ 'quotes-workspace--tool-active': showSelect }"
  >
    <QuotesListInsights>
      <template #summary>
        <div class="quotes-workspace__hero-meta">
          <div class="quotes-workspace__metric">
            <span>Current view</span>
            <strong>{{
              !canViewCount || resultCount === null ? '—' : resultCount.toLocaleString()
            }}</strong>
          </div>
          <div class="quotes-workspace__metric">
            <span>Selected</span>
            <strong>{{ selectedCount }}</strong>
          </div>
        </div>
      </template>
    </QuotesListInsights>

    <QuoteListToolbar
      :query-params="queryParams"
      :result-count="canViewCount ? resultCount : null"
      @search="handleSearch"
      @status-change="handleStatusChange"
      @filter-change="handleFilterChange"
      @date-range-change="handleDateRangeChange"
      @sort-change="handleSortChange"
      @limit-change="handleLimitChange"
      @clear="handleClearFilters"
    />

    <QuoteOperationsList
      ref="quoteOperationsList"
      :query-params="queryParams"
      :show-select="showSelect"
      :selected-quotes="quoteStore.selectedQuotes"
      @page-change="handlePageChange"
      @selection-change="handleSelectionChange"
      @set-inactive="handleSetInactive"
      @purchase-order="handlePurchaseOrderOpen"
      @loaded="handleListLoaded"
    >
      <template #actions>
        <div
          class="quotes-workspace__actions"
          data-testid="quote-workspace-actions"
        >
          <VBtn
            v-if="can(A.CREATE, Subject.QUOTES_CREATE)"
            color="primary"
            prepend-icon="tabler-plus"
            @click="handleNewQuoteNavigation"
          >
            New quote
          </VBtn>
          <VBtn
            v-if="can(A.CREATE, Subject.INVOICES_CREATE)"
            :color="isInvoiceMode ? 'primary' : 'secondary'"
            :prepend-icon="isInvoiceMode ? 'tabler-x' : 'tabler-file-invoice'"
            :variant="isInvoiceMode ? 'flat' : 'tonal'"
            @click="handleInvoiceMode"
          >
            {{ isInvoiceMode ? 'Stop invoicing' : 'Invoice quotes' }}
          </VBtn>
          <VBtn
            v-if="
              can(A.UPDATE, 'Quotes:Messages:Update:Guid') &&
              can(A.READ, Subject.MESSAGES_TEMPLATES_PREVIEW_ALL)
            "
            :color="isMessageMode ? 'primary' : 'secondary'"
            :prepend-icon="isMessageMode ? 'tabler-x' : 'tabler-mail-forward'"
            :variant="isMessageMode ? 'flat' : 'tonal'"
            @click="handleMessageMode"
          >
            {{ isMessageMode ? 'Stop messaging' : 'Message quotes' }}
          </VBtn>
          <VBtn
            v-if="can(A.UPDATE, Subject.QUOTES_UPDATE_GUID)"
            :color="isStatusMode ? 'primary' : 'secondary'"
            :prepend-icon="isStatusMode ? 'tabler-x' : 'tabler-status-change'"
            :variant="isStatusMode ? 'flat' : 'tonal'"
            @click="handleStatusMode"
          >
            {{ isStatusMode ? 'Stop updating' : 'Update statuses' }}
          </VBtn>
          <span>
            <VBtn
              prepend-icon="tabler-ruler-measure"
              variant="tonal"
              :disabled="!canOpenHoardingPlanCreator"
              @click="handleHoardingPlanDialogOpen"
            >
              Hoarding plan
            </VBtn>
            <VTooltip
              v-if="!canOpenHoardingPlanCreator"
              activator="parent"
              location="top"
            >
              {{ HOARDING_PLANNER_DENIED_TEXT }}
            </VTooltip>
          </span>
        </div>
      </template>
    </QuoteOperationsList>

    <ServiceListToolsPanel
      v-if="showSelect"
      :key="activeTool ?? undefined"
      :origin="toolOrigin"
      tool-label="quote"
      data-testid="quote-tools-panel"
      :title="
        isInvoiceMode ? 'Invoice quotes' : isMessageMode ? 'Message quotes' : 'Update statuses'
      "
      :selected-count="selectedCount"
      @close="handleCloseActiveTool"
    >
      <QuoteBulkInvoiceActions
        v-if="isInvoiceMode"
        hide-close
        :selected-count="selectedCount"
        :service-styles="quoteStore.serviceStyles"
        :update-status="setStatusOnSubmit"
        :status="invoiceStatusId"
        :is-loading="isInvoiceSubmitting"
        @invoice="handleInvoiceSelected"
        @cancel="handleCloseActiveTool"
        @update-status-change="handleUpdateStatusChange"
        @status-change="handleInvoiceStatusUpdate"
      />

      <QuoteBulkStatusActions
        v-if="isStatusMode"
        hide-close
        :selected-count="selectedCount"
        :status="bulkStatusId"
        :is-loading="isBulkStatusSubmitting"
        @apply="handleApplyBulkStatus"
        @cancel="handleCloseActiveTool"
        @status-change="handleBulkStatusUpdate"
      />

      <div
        v-if="isMessageMode"
        class="quotes-workspace__message-shell"
      >
        <TableMessagesTool
          compact
          class="quotes-workspace__message-tool"
          :title="PageMeta.Quotes.TableMessagesTool.Title"
          :selected-items="quoteStore.selectedQuotes"
          :selected-records="quoteStore.quotes ?? []"
          :service-type="ServiceType.Quotes"
          @cancel="handleCloseActiveTool"
          @reload-table="handleTableMessagesToolReload"
        />
      </div>
    </ServiceListToolsPanel>

    <HoardingPlanDialog
      v-if="isHoardingPlanDialogVisible && canOpenHoardingPlanCreator"
      :is-dialog-visible="isHoardingPlanDialogVisible"
      :service-type="Endpoint.Quotes.Name"
      :service-g-u-i-d="hoardingServiceGUID"
      :plan-g-u-i-d="hoardingPlanGUID"
      @update:is-dialog-visible="handleHoardingPlanDialogVisibility"
      @plan-changed="updatePlannerPlan"
    />
    <HoardingPlannerAccessDeniedDialog
      v-else-if="isHoardingPlannerDenied"
      :is-dialog-visible="isHoardingPlannerDenied"
      @update:is-dialog-visible="handleHoardingPlanDialogVisibility"
    />

    <QuotePurchaseOrderDialog
      v-if="selectedQuoteForPurchaseOrder"
      v-model:is-dialog-visible="isPurchaseOrderDialogVisible"
      :quote-data="selectedQuoteForPurchaseOrder"
      :title="
        canRecord(Action.READ, Subject.QUOTES_PREVIEW_ALL, selectedQuoteForPurchaseOrder, 'id')
          ? `Update Purchase Order - Quote #${selectedQuoteForPurchaseOrder.id ?? 'Unknown'}`
          : 'Update Purchase Order'
      "
      :subtitle="
        canRecord(
          Action.READ,
          Subject.QUOTES_PREVIEW_ALL,
          selectedQuoteForPurchaseOrder,
          QuoteField.CONTACT_DATA
        )
          ? `Enter the purchase order number for ${selectedQuoteForPurchaseOrder.contactData?.name ?? 'this quote'}`
          : 'Enter the purchase order number'
      "
      :is-loading="isPurchaseOrderLoading"
      @update="handlePurchaseOrderUpdate"
      @update:is-dialog-visible="handlePurchaseOrderDialogVisibility"
    />
  </section>
</template>

<style scoped lang="scss">
.quotes-workspace {
  display: grid;
  gap: 1.5rem;
  font-feature-settings: 'cv01', 'ss03';
}

.quotes-workspace--tool-active {
  padding-block-end: min(60dvh, 38rem);
}

.quotes-workspace__hero-meta {
  display: flex;
  flex: 0 0 auto;
  gap: 0.75rem;
}

.quotes-workspace__metric {
  display: grid;
  min-inline-size: 7rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgba(var(--v-theme-surface), 0.7);
  gap: 0.2rem;
  padding: 0.75rem 0.9rem;
}

.quotes-workspace__metric span {
  color: rgba(var(--v-theme-on-surface), 0.56);
  font-size: 0.6875rem;
  font-weight: 510;
  text-transform: uppercase;
}

.quotes-workspace__metric strong {
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: 1.25rem;
  font-weight: 590;
  letter-spacing: -0.03em;
}

.quotes-workspace__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding-block: 0.25rem;
}

.quotes-workspace__actions :deep(.v-btn) {
  min-block-size: 2.75rem;
  padding-inline: 1.1rem;
}

@media (max-width: 599px) {
  .quotes-workspace {
    gap: 1rem;
  }

  .quotes-workspace__hero-meta,
  .quotes-workspace__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .quotes-workspace__actions {
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 11rem), 1fr));
  }

  .quotes-workspace__actions :deep(.v-btn) {
    min-block-size: 3rem;
    inline-size: 100%;
  }

  .quotes-workspace__actions :deep(.v-btn:first-child) {
    grid-column: 1 / -1;
  }

  .quotes-workspace__metric {
    min-inline-size: 0;
    padding: 0.65rem 0.75rem;
  }

  .quotes-workspace__message-shell {
    overflow: hidden;
  }
}
</style>
